import { logger } from "../utils/logger";
import { getTabSessionToken } from "./tabSession";

/** Custom error class for API responses with status and parsed data. */
export class ApiError extends Error {
  status: number;
  data: unknown;
  retryAfter?: number;

  constructor(message: string, status: number, data: unknown = null, retryAfter?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.retryAfter = retryAfter;
  }
}

export interface ApiFetchInit extends RequestInit {
  timeout?: number;
}

export type AuthFetchOptions = RequestInit & {
  timeout?: number;
  /**
   * Skip the global "Server error (5xx)" toast. Set this when the caller
   * renders the server's own `detail` message, so the user is not shown both.
   */
  suppressServerErrorToast?: boolean;
};

interface ParsedApiError {
  detail?: string | { message?: string; code?: string };
  error?: { message?: string };
  message?: string;
}

/**
 * Message for a failed response. FastAPI handlers raise `detail` either as a
 * string or as a structured payload (`{code, message, ...}`), so both shapes
 * are unwrapped before falling back to the opaque status text.
 */
export const apiErrorMessage = (parsed: ParsedApiError | null, status: number): string => {
  const detail = parsed?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return parsed?.error?.message || parsed?.message || `Request failed (${status})`;
};

const headersToRecord = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) return {};
  if (headers instanceof Headers) return Object.fromEntries(headers.entries());
  if (Array.isArray(headers)) return Object.fromEntries(headers);
  return headers;
};

/** Throws if response is HTML instead of JSON (session/auth failure indicator). */
export function validateJsonResponse(response: Response) {
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('text/html')) {
    throw new Error(
      `API returned HTML instead of JSON (${response.status} ${response.statusText}). ` +
      `This usually indicates a session timing issue or authentication failure. ` +
      `URL: ${response.url}`
    );
  }
}

export async function apiFetch(input: RequestInfo | URL, init: ApiFetchInit = {}) {
  const controller = new AbortController();

  const { timeout: timeoutOverride, signal: callerSignal, ...fetchInit } = init;
  const t = Number(timeoutOverride);
  const timeout = Number.isFinite(t) && t > 0 ? t : 30000; // Default 30 second timeout

  controller.signal.addEventListener('abort', () => {
    logger.warn('apiFetch aborted', {
      url: input,
      method: fetchInit.method,
      timeout,
      reason: controller.signal.reason
    });
  });

  const timeoutId = setTimeout(() => controller.abort('timeout'), timeout);

  // Forward caller cancellation so unmounting/navigation aborts the request
  let callerAbortHandler: (() => void) | undefined;
  if (callerSignal) {
    callerAbortHandler = () => controller.abort(callerSignal.reason);
    if (callerSignal.aborted) {
      controller.abort(callerSignal.reason);
    } else {
      callerSignal.addEventListener('abort', callerAbortHandler, { once: true });
    }
  }

  try {
    // Inject tab-session token for per-tab HIPAA isolation
    const tabToken = getTabSessionToken();
    const tabHeaders: Record<string, string> = tabToken
      ? { ...headersToRecord(fetchInit.headers), 'X-Tab-Session': tabToken }
      : headersToRecord(fetchInit.headers);

    const withCreds: RequestInit = {
      ...fetchInit,
      credentials: 'include',
      signal: controller.signal,
      headers: tabHeaders,
    };

    return await fetch(input, withCreds);
  } finally {
    clearTimeout(timeoutId);
    if (callerAbortHandler && callerSignal) {
      callerSignal.removeEventListener('abort', callerAbortHandler);
    }
  }
}
export async function apiJson(url: RequestInfo | URL, init: ApiFetchInit = {}) {
  const resp = await apiFetch(url, init);
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`API ${resp.status} ${resp.statusText} ${text}`);
  }
  validateJsonResponse(resp);
  const text = await resp.text();
  if (!text) {
    return null;
  }
  return JSON.parse(text);
}

let runtimeApiBaseUrl: string | undefined;

/**
 * Set an optional runtime API origin for standalone embeds.
 * The main application continues to use VITE_API_URL or the local proxy.
 */
export const setRuntimeApiBaseUrl = (baseUrl?: string): void => {
  const normalized = baseUrl?.trim().replace(/\/+$/, '');
  runtimeApiBaseUrl = normalized || undefined;
};

/**
 * Build a full API URL. Returns relative paths in dev (Vite proxy) and
 * absolute paths in production using VITE_API_URL or an embed override.
 */
export const buildApiUrl = (path) => {
  const base = (runtimeApiBaseUrl || import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '');
  
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  let endpoint = path.startsWith('/') ? path : `/${path}`;
  
  endpoint = endpoint.replace(/^\/+api(\/+|$)/i, '/');
  
  endpoint = endpoint.replace(/^\/{2,}/, '/');
  
  // In development (localhost) WITHOUT an explicit VITE_API_URL, return relative
  // URLs so Vite's dev/preview proxy can forward /api/* to the backend.
  // When VITE_API_URL IS set (e.g. in production), fall through to the
  // absolute-URL path below so requests reach the backend directly.
  const hasExplicitApiUrl = !!(runtimeApiBaseUrl || import.meta.env.VITE_API_URL);
  const isLocalDev = !hasExplicitApiUrl &&
    typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  
  if (isLocalDev) {
    return `/api${endpoint}`;
  }
  
  // In production, use absolute URLs with the configured API base
  let apiBase;
  if (base.startsWith('http')) {
    apiBase = base.endsWith('/api') ? base : `${base}/api`;
  } else {
    const origin = window.location.origin.replace(/\/+$/, '');
    apiBase = base.endsWith('/api') ? `${origin}${base}` : `${origin}${base}/api`;
  }
  
  return `${apiBase}${endpoint}`;
};



/**
 * All API methods below use fetchWithAuth internally, providing:
 * - Auth headers (Authorization token from tokenStore)
 * - 401 automatic refresh
 * - 429 retry with exponential backoff + jitter
 * - Server error (5xx) toast notifications
 * - credentials: 'include' for cookie-based auth
 */

export const get = (endpoint, options = {}) =>
  fetchWithAuth(buildApiUrl(endpoint), { ...options, method: 'GET' });

export const post = async (endpoint: string, data: unknown = undefined, options: AuthFetchOptions = {}) => {
  const res = await fetchWithAuth(buildApiUrl(endpoint), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headersToRecord(options.headers) },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    let parsed = null;
    try { parsed = await res.clone().json(); } catch { parsed = null; }
    const retryAfter = Number(res.headers.get('retry-after')) || undefined;
    throw new ApiError(apiErrorMessage(parsed, res.status), res.status, parsed, retryAfter);
  }
  return res;
};

export const postFormData = (endpoint: string, formData?: FormData, options: AuthFetchOptions = {}) =>
  fetchWithAuth(buildApiUrl(endpoint), {
    ...options,
    method: 'POST',
    body: formData,
  });

export const putFormData = (endpoint: string, formData?: FormData, options: AuthFetchOptions = {}) =>
  fetchWithAuth(buildApiUrl(endpoint), {
    ...options,
    method: 'PUT',
    body: formData,
  });

export const put = (endpoint: string, data: unknown = undefined, options: AuthFetchOptions = {}) =>
  fetchWithAuth(buildApiUrl(endpoint), {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...headersToRecord(options.headers) },
    body: JSON.stringify(data),
  });

export const deleteRequest = (endpoint, options = {}) =>
  fetchWithAuth(buildApiUrl(endpoint), { ...options, method: 'DELETE' });

export const patch = async (endpoint: string, data: unknown = undefined, options: AuthFetchOptions = {}) => {
  const res = await fetchWithAuth(buildApiUrl(endpoint), {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...headersToRecord(options.headers) },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    let parsed = null;
    try { parsed = await res.clone().json(); } catch { parsed = null; }
    throw new ApiError(apiErrorMessage(parsed, res.status), res.status, parsed);
  }
  return res;
};

export const redirectToFrontendRoute= (path = '/login') => {
  const frontendOrigin = window.location.origin;  // dynamic, works on dev/prod
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${frontendOrigin}${cleanPath}`;
  logger.log("🔁 Redirecting to frontend:", url);
  window.location.href = url;
};



// ---------------------------------------------------------------------------
// Global auth-failure signal
// ---------------------------------------------------------------------------
// Debounced: once fired, suppresses duplicate dispatches for 2 s so a burst
// of parallel 401 responses doesn't trigger multiple full-page reloads.
let _authFailedAt = 0;
const AUTH_FAIL_DEBOUNCE_MS = 2000;

const PUBLIC_PATHS = [
  '/login', '/demo-login', '/demo', '/register', '/forgot-password',
  '/reset-password', '/book', '/subscribe', '/gated-demo', '/name-my-clinic',
  '/terms', '/privacy', '/portal/register', '/owner-login', '/dev',
  '/mfa-verify', '/oauth-callback', '/try-kindredly-free',
];

function isPublicPath(): boolean {
  const path = window.location.pathname;
  return PUBLIC_PATHS.some(p => path === p || path.startsWith(p + '/'))
    || /^\/[^/]+\/(?:login|portal\/login)$/.test(path);
}

/**
 * Dispatch a global event telling AuthContext the session is dead.
 * Only fires when the user is on a protected page and hasn't been
 * notified recently (debounce).
 */
export function signalAuthFailure(): void {
  const now = Date.now();
  if (now - _authFailedAt < AUTH_FAIL_DEBOUNCE_MS) return;
  if (isPublicPath()) return;
  _authFailedAt = now;
  window.dispatchEvent(new CustomEvent('auth:session-expired'));
}

/**
 * Attempt token refresh on 401. Returns true only when the server actually
 * issued a new token (2xx). Concurrent callers share one in-flight refresh:
 * the server rotates and blocklists the refresh token on every call, so two
 * parallel refreshes make the second one fail with a stale token and delete
 * the refresh cookie — logging the user out mid-session.
 */
let _refreshPromise: Promise<boolean> | null = null;

export const refreshAuth = (): Promise<boolean> => {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      // Use apiFetch so the X-Tab-Session header is included when the user has
      // a tab-scoped session, and the request respects cookie-based auth.
      const resp = await apiFetch(buildApiUrl('/auth/refresh'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 5000,
      });
      return resp.ok;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        logger.log('Auth refresh timeout - treating as failed refresh');
      } else {
        logger.error('Error refreshing authentication:', error);
      }
      return false;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
};

/** Fetch with auth headers, 401 refresh, and 429 retry with jitter. */
export const fetchWithAuth = async (
  url: RequestInfo | URL,
  options: AuthFetchOptions = {},
  retryOnAuth = true,
) => {
  const { getAccessToken, getTokenType } = await import('./tokenStore.js');
  const accessToken = getAccessToken();
  const tokenType = getTokenType();

  const {
    timeout: timeoutMs,
    signal: callerSignal,
    suppressServerErrorToast = false,
    ...baseOptions
  } = options;

  // Only apply an AbortController timeout when the caller explicitly
  // passes a `timeout` value.  Without an explicit timeout the request
  // runs until the server responds (matching the old fetchWithAuth
  // behaviour and avoiding surprise aborts for callers that never
  // specified a limit — chunked imports, file downloads, etc.).
  const controller = new AbortController();
  const t = Number(timeoutMs);
  const hasExplicitTimeout = Number.isFinite(t) && t > 0;
  const timeoutId = hasExplicitTimeout
    ? setTimeout(() => controller.abort('timeout'), t)
    : undefined;

  // Forward a caller-provided signal so external cancellation still works.
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort(callerSignal.reason);
    } else {
      callerSignal.addEventListener('abort', () => controller.abort(callerSignal.reason), { once: true });
    }
  }

  const baseHeaders = headersToRecord(baseOptions.headers);
  // Inject tab-session token for per-tab HIPAA isolation
  const tabToken = getTabSessionToken();
  const authHeaders = accessToken
    ? { ...baseHeaders, Authorization: `${tokenType} ${accessToken}` }
    : baseHeaders;
  const headers = tabToken
    ? { ...authHeaders, 'X-Tab-Session': tabToken }
    : authHeaders;

  const fetchOptions: RequestInit = {
    ...baseOptions,
    credentials: 'include',
    headers,
    signal: controller.signal,
  };

  const doFetch = async () => {
    const resp = await fetch(url, fetchOptions);
    if (resp.status >= 500 && !suppressServerErrorToast) {
      import('react-toastify').then(({ toast }) => {
        toast.error(
          `Server error (${resp.status}). Please try again or contact support if this persists.`,
          { toastId: `server-error-${resp.status}`, autoClose: 5000 }
        );
      }).catch(() => {});
    }
    return resp;
  };
  try {
    const response = await doFetch();

    if (response.status === 429) {
      // Do NOT retry 429s — retries consume more rate-limit budget and
      // amplify the throttling into a cascade where every parallel
      // request keeps the user over the limit.  Show a single toast
      // and let React Query's error state handle the UI.
      const retryHeader = response.headers.get('retry-after');
      let retryAfterSec = Number(retryHeader) || 0;
      if (!retryAfterSec) {
        try {
          const body = await response.clone().json();
          retryAfterSec = Number(body?.retry_after) || 0;
        } catch {
          retryAfterSec = 0;
        }
      }
      const retrySec = retryAfterSec || 60;
      import('react-toastify').then(({ toast }) => {
        toast.warning(
          `Too many requests — please wait ${retrySec}s and refresh.`,
          { toastId: 'rate-limit-429', autoClose: retrySec * 1000 }
        );
      }).catch(() => {});
      return response;
    }

    if (response.status === 401 && retryOnAuth) {
      if (accessToken) {
        const { clearTokens } = await import('./tokenStore.js');
        clearTokens();
        // Strip the stale Authorization header so the retry uses cookies.
        const h = fetchOptions.headers as Record<string, string> | undefined;
        if (h) delete h['Authorization'];
        const refreshed = await refreshAuth();
        if (refreshed) {
          return doFetch();
        }
        signalAuthFailure();
        return response;
      } else {
        const refreshed = await refreshAuth();
        if (refreshed) {
          return doFetch();
        }
        signalAuthFailure();
        return response;
      }
    }

    return response;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Download a file from an authenticated API endpoint.
 * Uses fetchWithAuth so the request includes auth headers and cookies,
 * unlike window.open() which sends a bare browser navigation.
 *
 * Opens a blank tab synchronously (before the async fetch) to avoid
 * popup blockers, then navigates it to the blob URL once ready.
 */
export const downloadApiFile = async (endpoint: string, filename?: string) => {
  // Open blank tab synchronously in the click-handler context so
  // browsers don't treat it as a blocked popup.
  const tab = window.open('about:blank', '_blank');

  try {
    const res = await fetchWithAuth(buildApiUrl(endpoint), { method: 'GET' });
    if (!res.ok) {
      let detail = `Download failed (${res.status})`;
      try {
        const body = await res.clone().json();
        if (body?.detail) detail = body.detail;
      } catch { /* ignore parse errors */ }
      throw new Error(detail);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);

    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/pdf')) {
      // Navigate the pre-opened tab to the blob URL
      if (tab) {
        tab.location.href = url;
      } else {
        // Fallback if popup was still blocked
        window.open(url, '_blank');
      }
    } else {
      // For other file types, trigger a download and close the blank tab
      tab?.close();
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }

    // Revoke after a delay to allow the browser to process
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    // Close the blank tab on error so it doesn't linger
    tab?.close();
    throw err;
  }
};

/** @deprecated Use get() directly - all methods now include auth. */
export const getWithAuth = get;
/** @deprecated Use post() directly - all methods now include auth. */
export const postWithAuth = post;
/** @deprecated Use put() directly - all methods now include auth. */
export const putWithAuth = put;
/** @deprecated Use patch() directly - all methods now include auth. */
export const patchWithAuth = patch;
/** @deprecated Use deleteRequest() directly - all methods now include auth. */
export const deleteWithAuth = deleteRequest;

export const withRetry = async (apiCall, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await apiCall();
      if (response.ok || response.status < 500) {
        return response;
      }
      throw new Error(`HTTP ${response.status}`);
    } catch (error) {
      if (attempt === maxRetries || (error.name !== 'AbortError' && !error.message.includes('HTTP 5'))) {
        throw error;
      }
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

export default {
  validateJsonResponse,
  apiFetch,
  apiJson,
  buildApiUrl,
  get,
  post,
  put,
  patch,
  postFormData,
  putFormData,
  deleteRequest,
  redirectToFrontendRoute,
  refreshAuth,
  fetchWithAuth,
  downloadApiFile,
  getWithAuth,
  postWithAuth,
  putWithAuth,
  patchWithAuth,
  deleteWithAuth,
  withRetry,
};
